import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import {
  useAgentProfileCreateControllerAgentProfileCreateForManagersMutation,
  useAgentProfileDeleteControllerAgentProfileDeleteForManagersMutation,
  useAgentProfileEditControllerAgentProfileEditForManagersMutation,
  useAgentProfileListControllerAgentProfileListForAdminsQuery,
} from '@shared/services/apiService/apiService';
import React, { useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { showProfileConflictToast } from '@shared/utils/agentProfileConflictMessage';

interface AgentProfilesTabContentProps {
  companyId?: string;
}

interface AgentProfileFormData {
  name: string;
  prompt: string;
  firstMessage: string;
  firstMessageInterruptionsEnabled: boolean;
}

interface FormErrors {
  name?: string;
  prompt?: string;
}

export const AgentProfilesTabContent: React.FC<AgentProfilesTabContentProps> = ({ companyId }) => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [currentProfile, setCurrentProfile] = useState<any>(null);
  const [formData, setFormData] = useState<AgentProfileFormData>({
    name: '',
    prompt: '',
    firstMessage: '',
    firstMessageInterruptionsEnabled: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const { t } = useTranslation();
  const { showError } = useErrorHandler();

  const translations = {
    prompt: t('superAdminMainPage.companies.profiles.prompt'),
    openingLine: t('superAdminMainPage.companies.profiles.openingLine'),
    openingLinePlaceholder: t('superAdminMainPage.companies.profiles.openingLinePlaceholder'),
    firstMessageInterruptionsEnabled: t(
      'superAdminMainPage.companies.profiles.firstMessageInterruptionsEnabled',
    ),
    profileName: t('superAdminMainPage.companies.profiles.profileName'),
    agentProfiles: t('superAdminMainPage.companies.profiles.agentProfiles'),
    createNewProfile: t('superAdminMainPage.companies.profiles.createNewProfile'),
    noAgentProfilesFound: t('superAdminMainPage.companies.profiles.noAgentProfilesFound'),
    name: t('superAdminMainPage.companies.profiles.name'),
    actions: t('superAdminMainPage.companies.profiles.actions'),
    createAgentProfile: t('superAdminMainPage.companies.profiles.createAgentProfile'),
    editAgentProfile: t('superAdminMainPage.companies.profiles.editAgentProfile'),
    deleteAgentProfile: t('superAdminMainPage.companies.profiles.deleteAgentProfile'),
    cancel: t('superAdminMainPage.companies.profiles.cancel'),
    create: t('superAdminMainPage.companies.profiles.create'),
    save: t('superAdminMainPage.companies.profiles.save'),
    delete: t('superAdminMainPage.companies.profiles.delete'),
    nameRequired: t('superAdminMainPage.companies.profiles.nameRequired'),
    promptRequired: t('superAdminMainPage.companies.profiles.promptRequired'),
    deleteConfirmation: t('superAdminMainPage.companies.profiles.deleteConfirmation'),
  };

  const {
    data: agentProfiles,
    isLoading,
    refetch,
  } = useAgentProfileListControllerAgentProfileListForAdminsQuery(
    { id: companyId || '' },
    { skip: !companyId },
  );

  const [createAgentProfile, { isLoading: isCreating }] =
    useAgentProfileCreateControllerAgentProfileCreateForManagersMutation();
  const [editAgentProfile, { isLoading: isEditing }] =
    useAgentProfileEditControllerAgentProfileEditForManagersMutation();
  const [deleteAgentProfile, { isLoading: isDeleting }] =
    useAgentProfileDeleteControllerAgentProfileDeleteForManagersMutation();

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = translations.nameRequired;
    }

    if (!formData.prompt.trim()) {
      newErrors.prompt = translations.promptRequired;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      prompt: '',
      firstMessage: '',
      firstMessageInterruptionsEnabled: false,
    });
    setErrors({});
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (profile: any) => {
    setCurrentProfile(profile);
    setFormData({
      name: profile.name || '',
      prompt: profile.prompt || '',
      firstMessage: profile.firstMessage ?? '',
      firstMessageInterruptionsEnabled: profile.firstMessageInterruptionsEnabled ?? false,
    });
    setErrors({});
    setOpenEditDialog(true);
  };

  const handleOpenDeleteConfirm = (profile: any) => {
    setCurrentProfile(profile);
    setOpenDeleteConfirm(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePromptChange = (value: string) => {
    setFormData((prev) => ({ ...prev, prompt: value }));

    if (errors.prompt) {
      setErrors((prev) => ({ ...prev, prompt: undefined }));
    }
  };

  const handleFirstMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, firstMessage: e.target.value }));
  };

  const handleCreateProfile = async () => {
    if (!companyId || !validateForm()) return;

    try {
      const normalizedFirstMessage = formData.firstMessage.trim();

      await createAgentProfile({
        id: companyId,
        agentProfileCreateAdminRequestDto: {
          name: formData.name,
          prompt: formData.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: formData.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.created', {
            name: formData.name,
          }),
        },
      });

      setOpenCreateDialog(false);
      refetch();
    } catch (error) {
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.createTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.createSuggestion'),
      });
    }
  };

  const handleEditProfile = async () => {
    if (!companyId || !currentProfile?.id || !validateForm()) return;

    try {
      const normalizedFirstMessage = formData.firstMessage.trim();

      await editAgentProfile({
        id: companyId,
        profileId: currentProfile.id,
        agentProfileEditRequestDto: {
          name: formData.name,
          prompt: formData.prompt,
          firstMessage: normalizedFirstMessage === '' ? null : normalizedFirstMessage,
          firstMessageInterruptionsEnabled: formData.firstMessageInterruptionsEnabled,
        },
      }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.updated', {
            name: formData.name,
          }),
        },
      });

      setOpenEditDialog(false);
      refetch();
    } catch (error) {
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.updateTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.updateSuggestion'),
      });
    }
  };

  const handleDeleteProfile = async () => {
    if (!currentProfile?.id || !companyId) return;

    try {
      await deleteAgentProfile({ id: companyId, profileId: currentProfile.id }).unwrap();

      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('common.success'),
          description: t('superAdminMainPage.companies.profiles.messages.deleted', {
            name: currentProfile.name,
          }),
        },
      });

      setOpenDeleteConfirm(false);
      refetch();
    } catch (error) {
      if (
        showProfileConflictToast(
          error,
          t,
          t('superAdminMainPage.companies.profiles.errors.deleteTitle'),
          t('superAdminMainPage.companies.profiles.errors.deleteSuggestion'),
        )
      ) {
        return;
      }
      showError({
        title: t('superAdminMainPage.companies.profiles.errors.deleteTitle'),
        error,
        suggestion: t('superAdminMainPage.companies.profiles.errors.deleteSuggestion'),
      });
    }
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{translations.agentProfiles}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
          disabled={!companyId}>
          {translations.createNewProfile}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : !agentProfiles || agentProfiles.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }}>
          {translations.noAgentProfilesFound}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{translations.name}</TableCell>
                <TableCell>{translations.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agentProfiles.map((profile) => (
                <TableRow key={profile.id}>
                  <TableCell>{profile.name}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenEditDialog(profile)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleOpenDeleteConfirm(profile)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Dialog */}
      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="lg"
        fullWidth>
        <DialogTitle>{translations.createAgentProfile}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={translations.profileName}
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            required
            sx={{ mb: errors.name ? 0.5 : 2 }}
          />
          {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
          <MarkdownEditor
            value={formData.prompt}
            onChange={handlePromptChange}
            label={translations.prompt}
            error={!!errors.prompt}
            helperText={errors.prompt}
            required
            height={600}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translations.openingLine}
            </Typography>
            <TextField
              margin="dense"
              name="firstMessage"
              type="text"
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder={translations.openingLinePlaceholder}
              value={formData.firstMessage}
              onChange={handleFirstMessageChange}
            />
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <CustomSwitch
                sx={{ ml: '20px', mr: '14px' }}
                checked={formData.firstMessageInterruptionsEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({
                    ...prev,
                    firstMessageInterruptionsEnabled: e.target.checked,
                  }))
                }
              />
            }
            label={translations.firstMessageInterruptionsEnabled}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>{translations.cancel}</Button>
          <Button
            onClick={handleCreateProfile}
            variant="contained"
            color="primary"
            disabled={isCreating}>
            {isCreating ? <CircularProgress size={24} /> : translations.create}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="lg"
        fullWidth>
        <DialogTitle>{translations.editAgentProfile}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={translations.profileName}
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            required
            sx={{ mb: errors.name ? 0.5 : 2 }}
          />
          {errors.name && <FormHelperText error>{errors.name}</FormHelperText>}
          <MarkdownEditor
            value={formData.prompt}
            onChange={handlePromptChange}
            label={translations.prompt}
            error={!!errors.prompt}
            helperText={errors.prompt}
            required
            height={600}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {translations.openingLine}
            </Typography>
            <TextField
              margin="dense"
              name="firstMessage"
              type="text"
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder={translations.openingLinePlaceholder}
              value={formData.firstMessage}
              onChange={handleFirstMessageChange}
            />
          </Box>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <CustomSwitch
                sx={{ ml: '20px', mr: '14px' }}
                checked={formData.firstMessageInterruptionsEnabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData((prev) => ({
                    ...prev,
                    firstMessageInterruptionsEnabled: e.target.checked,
                  }))
                }
              />
            }
            label={translations.firstMessageInterruptionsEnabled}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>{translations.cancel}</Button>
          <Button
            onClick={handleEditProfile}
            variant="contained"
            color="primary"
            disabled={isEditing}>
            {isEditing ? <CircularProgress size={24} /> : translations.save}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>{translations.deleteAgentProfile}</DialogTitle>
        <DialogContent>
          <Typography>
            {translations.deleteConfirmation.replace('{name}', currentProfile?.name || '')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>{translations.cancel}</Button>
          <Button
            onClick={handleDeleteProfile}
            variant="contained"
            color="error"
            disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : translations.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
